import React, { useCallback, useState } from "react";
import moment from "moment";

import {
  TableRow,
  TableCell,
  Table,
  TableContainer,
  TableBody,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  IconButton,
} from "@material-ui/core";

import { EnhancedTableHead } from "./index";

import excelIcon from "../../../../assets/images/icons/excel.svg";
import axios from "../../../../axios";

const accountHeadCells = [
  {
    id: "i",
    numeric: false,
    disablePadding: false,
    label: "Sl.No",
  },
  {
    id: "company",
    numeric: false,
    disablePadding: false,
    label: "Company Name",
  },
  {
    id: "invoiceNo",
    numeric: false,
    disablePadding: false,
    label: "Booking Ref",
  },
  {
    id: "clientName",
    numeric: false,
    disablePadding: false,
    label: "Date of attendance",
  },
  {
    id: "customer",
    numeric: false,
    disablePadding: false,
    label: "Invoice No / Invoice Date",
  },
  {
    id: "language",
    numeric: false,
    disablePadding: false,
    label: "Language / Service",
  },
  {
    id: "interpreter",
    numeric: false,
    disablePadding: false,
    label: "Invoice Amount",
  },
  {
    id: "attendance",
    numeric: false,
    disablePadding: false,
    label: "Date Paid",
  },
  {
    id: "invoiceAmount",
    numeric: false,
    disablePadding: false,
    label: "Paid Amount (£)",
  },
  {
    id: "outstanding",
    numeric: false,
    disablePadding: false,
    label: "Outstanding (£)",
  },
  {
    id: "ipPayment",
    numeric: false,
    disablePadding: false,
    label: "IP Payment",
  },
  {
    id: "ip",
    numeric: false,
    disablePadding: false,
    label: "IP",
  },
  {
    id: "serviceCharges",
    numeric: true,
    disablePadding: false,
    label: "Service Charges",
  },
  {
    id: "vatAmount",
    numeric: true,
    disablePadding: false,
    label: "VAT",
  },
  {
    id: "account",
    numeric: true,
    disablePadding: true,
    label: "LIL Account (£)",
  },
];

const styles = {
  fontSize: "13px",
  padding: "6px",
};

const CardItem = ({
  title,
  active,
  classes,
  data,
  isLoading,
  matches,
  hasData,
  values,
}) => {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("company");
  const [downloading, setDownloading] = useState(false);

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order],
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = data.map((n) => n.name);
        setSelected(newSelecteds);
        return;
      }

      setSelected([]);
    },
    [data],
  );

  const downloadHandler = async () => {
    try {
      setDownloading(true);

      let query = "";

      if (values.companyId) {
        query += `companyId=${values.companyId}`;
      }

      if (values.businessType) {
        query += `${query ? "&" : ""}businessType=${values.businessType}`;
      }

      if (values.fromDate) {
        query += `${query ? "&" : ""}fromDate=${values.fromDate}`;
      }

      if (values.toDate) {
        query += `${query ? "&" : ""}toDate=${values.toDate}`;
      }

      const response = await axios.get(
        `/api/v1/bookings/acstatement/download?${query}`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute(
        "download",
        `LIL_Account_Statement_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className={active}>
      <CardContent style={{ padding: "8px 6px" }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          style={{ padding: "6px" }}
        >
          <Grid item xs>
            <Typography variant="body1" noWrap>
              {title}
            </Typography>
          </Grid>

          {hasData && (
            <Grid item>
              <Tooltip title="Download Excel">
                <span>
                  <IconButton
                    size="medium"
                    onClick={downloadHandler}
                    disabled={downloading || data.length === 0}
                  >
                    <img
                      src={excelIcon}
                      alt="excel"
                      style={{
                        width: 25,
                        height: 25,
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <TableContainer
          className={matches ? classes.container2 : classes.container}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            className={classes.table}
            size="small"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              headCells={accountHeadCells}
            />

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center" }}
                    rowSpan={6}
                    colSpan={15}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow hover>
                  <TableCell
                    style={{ textAlign: "center" }}
                    rowSpan={6}
                    colSpan={15}
                  >
                    No record
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => {
                  const amount = row?.invTotal - row?.balanceAmount;

                  const lilamount =
                    row?.invTotal - row?.IPPaid - row?.vatAmount;

                  return (
                    <TableRow hover key={row._id + index}>
                      <TableCell style={styles}>{index + 1}</TableCell>

                      <TableCell style={styles} component="th" scope="row">
                        <b>
                          {row.businessType === "Private"
                            ? "Private"
                            : row.companyName || row.company}
                        </b>
                      </TableCell>

                      <TableCell style={styles}>
                        {row.booking_ref || row.bookingRef}
                      </TableCell>

                      <TableCell style={styles} align="left">
                        {moment(row.date).format("DD/MM/YYYY")}
                      </TableCell>

                      <TableCell style={styles} align="left">
                        {row.invoiceNo}
                        <br />
                        <small>
                          {moment(row.invoiceDate).format("DD/MM/YYYY")}
                        </small>
                      </TableCell>

                      <TableCell style={styles} align="left">
                        <strong>{row.language}</strong>
                        <br />
                        <small>{row.serviceType}</small>
                      </TableCell>

                      <TableCell style={styles} align="left">
                        {row?.invTotal?.toFixed(2)}
                      </TableCell>

                      <TableCell style={styles} align="left">
                        {moment(row.paiddate).format("DD/MM/YYYY")}
                      </TableCell>

                      <TableCell style={styles} align="left">
                        {amount?.toFixed(2)}
                      </TableCell>

                      <TableCell style={styles} align="left">
                        {row?.balanceAmount?.toFixed(2)}
                      </TableCell>

                      <TableCell style={styles} align="left">
                        {row?.IPPaid?.toFixed(2)}
                      </TableCell>

                      <TableCell style={styles} align="left">
                        <b>{row?.interpreterName}</b>
                      </TableCell>

                      <TableCell style={styles} align="right">
                        {row?.serviceCharges?.toFixed(2)}
                      </TableCell>

                      <TableCell style={styles} align="right">
                        {row?.vatAmount?.toFixed(2)}
                      </TableCell>

                      <TableCell style={styles} align="right">
                        {lilamount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default React.memo(CardItem);
